// src/components/Navbar.js
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { fetchOrganizations, getSelectedOrgId } from "../services/api";

function LogoImage({ rawLogo, orgName }) {
  const [idx, setIdx] = useState(0);

  // Build a list of candidate URLs to try, with cache-bust to avoid stale 304s.
  const candidates = useMemo(() => {
    if (!rawLogo) return [];
    const ensureLeadingSlash = (p) =>
      p.startsWith("http://") || p.startsWith("https://") || p.startsWith("/")
        ? p
        : `/${p}`;

    // base (as provided) + cache bust
    const base = ensureLeadingSlash(rawLogo);
    const ts = Date.now();
    const withBust = base.includes("?") ? `${base}&_=${ts}` : `${base}?_=${ts}`;

    // If it already has an image extension, also try alternate common extensions in case DB ext is wrong.
    const m = base.match(/\.(png|jpg|jpeg|gif|webp|svg|bmp|ico|avif)(\?.*)?$/i);
    const exts = ["png", "jpg", "jpeg", "webp", "gif", "svg", "avif", "bmp", "ico"];

    let list = [withBust, base]; // try with bust first, then raw (just in case)

    const withoutQuery = base.split("?")[0];
    if (m) {
      // eslint fix: no need to escape "/" inside the character class
      const stem = withoutQuery.replace(/\.[^./]+$/, ""); // remove the existing extension
      for (const e of exts) {
        const candidate = `${stem}.${e}?_=${ts}`;
        if (!list.includes(candidate)) list.push(candidate);
      }
    } else {
      // No extension in DB path — try common image extensions
      for (const e of exts) {
        list.push(`${withoutQuery}.${e}?_=${ts}`);
      }
    }
    return list;
  }, [rawLogo]);

  const src = candidates[idx] || null;

  if (!src) {
    // Fallback placeholder
    return (
      <div className="h-8 w-8 bg-gray-300 rounded flex items-center justify-center text-gray-600 font-semibold">
        {orgName?.[0] ?? "?"}
      </div>
    );
  }

  return (
    <img
      key={src}
      src={src}
      alt="Org"
      className="object-contain rounded"
      style={{ maxWidth: 96, maxHeight: 96, width: "auto", height: "auto" }}
      onError={() => {
        // Try next candidate on error
        setIdx((i) => (i + 1 < candidates.length ? i + 1 : i));
      }}
    />
  );
}

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const role = (user?.group_role || "").toLowerCase();
  const isSuper = role === "superadmin";
  const isAdmin = role === "admin";

  const canSeeReports = ["superadmin", "admin", "hr", "hiring_manager"].includes(role);
  const canSeeOrganizations = isSuper;
  const canSeeOrgUsers = isSuper || isAdmin;

  const [orgs, setOrgs] = useState([]);
  useEffect(() => {
    fetchOrganizations()
      .then((d) => setOrgs(Array.isArray(d) ? d : []))
      .catch(() => setOrgs([]));
  }, []);

  const selectedOrgId = getSelectedOrgId();
  const currentOrg = useMemo(() => {
    const sid = selectedOrgId != null ? Number(selectedOrgId) : null;
    if (sid != null) {
      const found =
        (orgs || []).find((o) => Number(o.id ?? o.organization_id) === sid) || null;
      if (found) return found;
    }
    return user?.organization || null;
  }, [orgs, selectedOrgId, user]);

  const rawLogo = currentOrg?.logo_url || currentOrg?.logoUrl || null; // original field usage kept
  const orgName = currentOrg?.name || currentOrg?.organization_name || "System Dashboard";

  const isActive = (to) =>
    pathname === to || pathname.startsWith(to + "/")
      ? "text-blue-600 font-semibold"
      : "hover:text-blue-600";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const displayName = user
    ? `${user.user_name ?? user.name ?? ""} (${user.username ?? ""})`
    : "";

  return (
    <nav className="bg-white border-b shadow-sm text-gray-800">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-6">
        {/* Org badge (logo + name) */}
        <div className="flex items-center gap-3">
          {rawLogo ? (
            <LogoImage rawLogo={rawLogo} orgName={orgName} />
          ) : (
            <div className="h-8 w-8 bg-gray-300 rounded flex items-center justify-center text-gray-600 font-semibold">
              {orgName?.[0] ?? "?"}
            </div>
          )}
          <span className="font-semibold">{orgName}</span>
        </div>

        {/* Main nav */}
        <div className="flex items-center gap-4">
          <Link to="/" className={isActive("/")}>Dashboard</Link>
          <Link to="/candidates" className={isActive("/candidates")}>Candidates</Link>
          <Link to="/jobs" className={isActive("/jobs")}>Jobs</Link>
          <Link to="/interviews" className={isActive("/interviews")}>Interviews</Link>
          {canSeeReports && <Link to="/reports" className={isActive("/reports")}>Reports</Link>}
          {canSeeOrgUsers && <Link to="/org-users" className={isActive("/org-users")}>Organization Users</Link>}
          {canSeeOrganizations && <Link to="/organizations" className={isActive("/organizations")}>Organizations</Link>}
        </div>

        <div className="ml-auto flex items-center gap-3">
          {user && (
            <button
              type="button"
              className="text-sm text-gray-700 hover:text-blue-700 underline-offset-2 hover:underline"
              onClick={() => navigate("/profile")}
              title="Open your profile"
            >
              {displayName}
            </button>
          )}
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
}
